import React, { useEffect, useMemo, useState } from 'react';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { routeConstants } from '../../../constants/routeConstants';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Accordion, Breadcrumbs, Button, ButtonDropdown } from '../../../components/shared';
import useStyles from './style';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { rentDetailsFormAction, leaseDetailsFormAction, setSearchApiParamsAction } from '../../../redux-slice/lease';
import LeaseDetailsAccordion from './LeaseDetailsAccordion';
import ApprovalRequestModal from './ApprovalRequestModal';
import TimerModal from '../../../components/TimerModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ConfirmationModal from '../../../components/ConfirmationModel/ConfirmationModal';
import TerminationRequestModal from './TerminationRequestModal';
import { useGetLeaseByIdQuery, useUpdateLeaseByIdMutation } from '../../../services/Lease/LeaseService';
//import { dateFormatForApi } from '../../../utils/common-methods';
import dayjs from 'dayjs';
import LeaseHistory from '../LeaseHistory/LeaseHistory';
import Modal from '../../../components/shared/Modal';

const LeaseDetails = () => {
  const {
    LEASE: { LEASE, LEASE_DETAILS },
  } = BREADCRUMB_NAMES;

  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const leaseId = Number(params.id);
  const updatedById = useSelector((state) => state.user?.userDetails?.id);
  const searchApiParams = {
    pending: 'processStatus=&locationId=&leaseStatus=PENDING',
    active: 'processStatus=&locationId=&leaseStatus=ACTIVE',
    archive: 'processStatus=&locationId=&leaseStatus=ARCHIVE',
  };
  const [statusModals, setStatusModals] = useState({
    ongoing: false,
    reject_lease: false,
    expired: false,
    terminate: false,
  });
  const [timerModals, setTimerModals] = useState({
    approved: false,
    rejected: false,
    expired: false,
    terminated: false,
  });
  const [time, setTime] = useState(5);
  const [activateTimer, setActivateTimer] = useState(false);
  const leaseState = useSelector((state) => state?.lease?.leaseForms);
  const { leaseDetails } = leaseState;
  const { currentData: leaseDetail = {}, isLoading, isFetching } = useGetLeaseByIdQuery(leaseId);
  const [updateLease] = useUpdateLeaseByIdMutation();
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const breadcrumbData = useMemo(
    () => [
      {
        text: LEASE,
        redirection: `${routeConstants.LEASE_ROUTE}?selectedTab=${
          leaseDetail?.leaseStatus === 'ARCHIVE' ? 2 : leaseDetail?.leaseStatus === 'ACTIVE' ? 1 : 2
        }`,
      },
      { text: LEASE_DETAILS },
    ],
    [leaseDetail?.leaseStatus],
  );

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
  };

  const uiButtonDropOptions = [
    { value: 'ONGOING', text: 'Active' },
    { value: 'TERMINATE', text: 'Archive' },
  ];

  useEffect(() => {
    const modelTypes = leaseDetail?.contractAssetGameCategoryRates?.map((value) => ({
      id: value?.gameCategory?.id,
      name: value?.gameCategory?.name,
      rate: value?.rentRate,
    }));
    dispatch(
      leaseDetailsFormAction({
        ...leaseDetails,
        location: leaseDetail?.location,
        leaseBegins: leaseDetail?.termStartDate ? dayjs(leaseDetail?.termStartDate).toString() : null,
        leaseEnds: leaseDetail?.termEndDate ? dayjs(leaseDetail?.termEndDate).toString() : null,
      }),
    );
    dispatch(
      rentDetailsFormAction({
        rentType: leaseDetail?.rentType || 'FEE_PLUS_RATE',
        rentFee: leaseDetail?.fee || 0.0,
        rentCalculationTiming: leaseDetail?.rentCalculateInterval || '',
        rentRate: leaseDetail?.rate || 0.0,
        calculationStarts: null, //not available in API
        calculationEnds: null, //not available in API
        breakpointNatural: leaseDetail?.hasNaturalBreakPoint,
        breakpointValue: leaseDetail?.naturalBreakPointValue || 0.0,
        breakpointRate: leaseDetail?.breakpointRate || 0.0,
        CAMFee: leaseDetail?.CAMFee || 0.0,
        CAMRate: leaseDetail?.CAMRate || 0.0,
        modalTypes: modelTypes,
        thresholdRate: leaseDetail?.thresholdRate || 0.0,
        thresholdRevenue: leaseDetail?.thresholdRevenue || 0.0,
        notes: leaseDetail?.notes || '',
        contractLink: leaseDetail?.contractLink || '',
        coinCost: leaseDetail?.coinCost || 0.0,
        cardCost: leaseDetail?.cardCost || 0.0,
        resetTerm: leaseDetail?.resetTerm || '',
        breakpointRevenue: leaseDetail?.breakpointRevenue || '',
        payFrequency: leaseDetail?.payFrequency || '',
        tierSummaryState: leaseDetail?.tierSummary || [],
        tierBreakpointAndRate: leaseDetail?.tierSummary || [],
      }),
    );
  }, [JSON.stringify(leaseDetail)]);

  const closeStatusModelsAndOpenTimerModals = (status) => {
    switch (status) {
      case 'ONGOING':
        closeStatusModals('ongoing');
        openTimerModal('approved');
        break;
      case 'REJECT_LEASE':
        closeStatusModals('reject_lease');
        openTimerModal('rejected');
        break;
      case 'EXPIRED':
        closeStatusModals('expired');
        openTimerModal('expired');
        break;
      case 'TERMINATE':
        closeStatusModals('terminate');
        openTimerModal('terminated');
        break;
    }
  };

  const updateLeaseApiCalling = (data) => {
    updateLease(data).then((res) => {
      if (res.data) {
        closeStatusModelsAndOpenTimerModals(data.processStatus);
      }
    });
  };

  const openModalOnSelectedValue = (value) => {
    if (value === 'ONGOING' || value === 'REJECT_LEASE' || value === 'EXPIRED' || value === 'TERMINATE') {
      setStatusModals((prevValue) => ({
        ...prevValue,
        [value.toLowerCase()]: true,
      }));
    } else {
      dispatch(setSearchApiParamsAction(searchApiParams.active));
      const data = {
        id: leaseId,
        processStatus: value,
        leaseStatus: 'ACTIVE',
        updatedBy: updatedById,
      };
      updateLeaseApiCalling(data);
    }
  };

  const historyLocationDetails = () => {
    setShowHistoryModal(true);
  };

  const onButtonChangeHandler = (selected) => {
    openModalOnSelectedValue(selected?.value);
  };

  const activateTimerWithTime = () => {
    setActivateTimer(true);
    setTime(5);
  };

  const openTimerModal = (name) => {
    activateTimerWithTime();
    setTimeout(() => {
      setTimerModals((prevValue) => ({ ...prevValue, [name]: true }));
    }, 300);
  };

  const closeTimerModal = (name) => {
    setActivateTimer(false);
    setTimerModals((prevValue) => ({ ...prevValue, [name]: false }));

    if (name === 'approved') {
      dispatch(setSearchApiParamsAction(searchApiParams.active));
      navigate(`/${routeConstants.LEASE_ROUTE}?selectedTab=1`, {
        replace: true,
      });
    } else if (name === 'rejected' || name === 'expired' || name === 'terminated') {
      dispatch(setSearchApiParamsAction(searchApiParams.archive));
      navigate(`/${routeConstants.LEASE_ROUTE}?selectedTab=2`, {
        replace: true,
      });
    }
  };

  const closeStatusModals = (name) => {
    setStatusModals((prevValue) => ({ ...prevValue, [name]: false }));
  };

  const onApproveStatusHandler = () => {
    const data = {
      id: leaseId,
      processStatus: 'ONGOING',
      leaseStatus: 'ACTIVE',
      updatedBy: updatedById,
    };
    updateLeaseApiCalling(data);
  };

  const onRejectLeaseModalClickHandler = (value) => {
    if (value === 'REJECT_LEASE') {
      const data = {
        id: leaseId,
        processStatus: value,
        leaseStatus: 'ARCHIVE',
        updatedBy: updatedById,
      };
      updateLeaseApiCalling(data);
    } else {
      closeStatusModals('reject_lease');
    }
  };

  const onExpiredModalClickHandler = (value) => {
    if (value === 'EXPIRED') {
      const data = {
        id: leaseId,
        processStatus: value,
        leaseStatus: 'ARCHIVE',
      };
      updateLeaseApiCalling(data);
    } else {
      closeStatusModals('expired');
    }
  };

  const terminationSubmitHandler = () => {
    const data = {
      id: leaseId,
      processStatus: 'TERMINATE',
      leaseStatus: 'ARCHIVE',
      updatedBy: updatedById,
    };
    updateLeaseApiCalling(data);
  };

  return (
    <Box className={classes.root}>
      <Box>
        <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      </Box>

      <Box mb={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box>
            <Stack direction={'row'} alignItems="center" spacing={1.5}>
              <Typography variant="h1" data-testid="leaseName">
                {leaseDetail?.lease_name}
              </Typography>
              <Chip
                label={leaseDetail?.leaseStatus}
                color={
                  leaseDetail?.leaseStatus === 'PENDING'
                    ? 'primary'
                    : leaseDetail?.leaseStatus === 'ACTIVE'
                    ? 'success'
                    : 'error'
                }
                variant="outlined"
                data-testid="status"
              />
            </Stack>
          </Box>
          <Box>
            <ButtonDropdown
              options={uiButtonDropOptions}
              value={leaseDetail?.leaseStatus === 'ACTIVE' ? 'ONGOING' : 'TERMINATE'}
              onChange={onButtonChangeHandler}
            />
          </Box>
        </Stack>
      </Box>
      <Box mb={3}>
        <Accordion
          defaultExpanded
          marginBottom={3}
          title={'Lease Details'}
          content={
            isLoading || isFetching ? (
              <LoadingSpinner containerHeight={400} />
            ) : (
              <LeaseDetailsAccordion leaseDetails={leaseDetail} isFrom={'LEASE'} />
            )
          }
          subComponent={<Button onClick={historyLocationDetails}>History</Button>}
        />
      </Box>
      <ApprovalRequestModal
        isOpen={statusModals.ongoing}
        approveClick={onApproveStatusHandler}
        onCancelClick={() => closeStatusModals('ongoing')}
      />
      {timerModals.approved && (
        <TimerModal
          isOpen={timerModals.approved}
          timer={time}
          activate={activateTimer}
          onTimeComplete={() => {
            closeTimerModal('approved');
          }}
          icon={<CheckCircleIcon fontSize="large" className={classes.successIcon} />}
          title="Successful!"
          msg="You have successfully approved the lease, and it is now active You will be redirected in"
        />
      )}
      {timerModals.rejected && (
        <TimerModal
          isOpen={timerModals.rejected}
          timer={time}
          activate={activateTimer}
          onTimeComplete={() => {
            closeTimerModal('rejected');
          }}
          icon={<WarningIcon fontSize="large" className={classes.dangerIcon} />}
          title="Rejected!"
          msg="This lease is now rejected as per your request You will be redirected in"
        />
      )}
      {timerModals.expired && (
        <TimerModal
          isOpen={timerModals.expired}
          timer={time}
          activate={activateTimer}
          onTimeComplete={() => {
            closeTimerModal('expired');
          }}
          icon={<WarningIcon fontSize="large" className={classes.dangerIcon} />}
          title="Expired!"
          msg="This lease is now marked as expired You will be redirected in"
        />
      )}
      {timerModals.terminated && (
        <TimerModal
          isOpen={timerModals.terminated}
          timer={time}
          activate={activateTimer}
          onTimeComplete={() => {
            closeTimerModal('terminated');
          }}
          icon={<WarningIcon fontSize="large" className={classes.dangerIcon} />}
          title="Archived!"
          msg="This lease is now archived as per your request You will be redirected in"
        />
      )}

      <ConfirmationModal
        isOpen={statusModals.reject_lease}
        title="Confirmation"
        msg="Are you sure you want to reject this lease? This cannot be recovered"
        buttons={[
          { text: 'Reject', value: 'REJECT_LEASE' },
          { text: 'Cancel', value: 'CANCEL' },
        ]}
        onClick={onRejectLeaseModalClickHandler}
      />
      <ConfirmationModal
        isOpen={statusModals.expired}
        title="Confirmation"
        msg="Are you sure you want to mark this lease as expired? It can be retrieve from archive tab later"
        buttons={[
          { text: 'Expired', value: 'EXPIRED' },
          { text: 'Cancel', value: 'CANCEL' },
        ]}
        onClick={onExpiredModalClickHandler}
      />
      {showHistoryModal && (
        <ConfirmationModal
          isOpen={showHistoryModal}
          title="Lease History"
          msg="This is a placeholder for lease history details."
          buttons={[{ text: 'Close', value: 'CLOSE' }]}
          onClick={closeHistoryModal}
        />
      )}
      {showHistoryModal && (
        <Modal
          open={showHistoryModal}
          onClose={closeHistoryModal}
          title={`Lease History of ${leaseDetail?.lease_name}`}
          titleVariant="subtitle10"
          closeIcon={true}
          PaperProps={{
            sx: {
              width: '90%',
              maxWidth: 'none',
              maxHeight: '90%',
              height: '70%',
              overflow: 'hidden',
            },
          }}
        >
          <LeaseHistory leaseId={leaseId} open={showHistoryModal} onClose={closeHistoryModal} />
        </Modal>
      )}
      <TerminationRequestModal
        isOpen={statusModals.terminate}
        submitClick={terminationSubmitHandler}
        onCancelClick={() => closeStatusModals('terminate')}
      />
    </Box>
  );
};

export default LeaseDetails;
